import React, { useState, useEffect, useCallback } from 'react';
import { 
  Sparkles, Users, Calendar, TrendingUp, MessageSquare, 
  Image, Clock, Settings, Plus, Trash2, Send, RefreshCw,
  Target, Zap, BarChart3, FileText, Download, Eye
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const MarketingDashboard = () => {
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  
  // Form states
  const [newClient, setNewClient] = useState({
    name: '', industry: '', description: '', brand_voice: 'professional', target_audience: '',
    social_handles: { instagram: '', twitter: '', facebook: '' }
  });
  const [campaignPrompt, setCampaignPrompt] = useState('');
  const [campaignDays, setCampaignDays] = useState(30);
  const [campaignPlatforms, setCampaignPlatforms] = useState(['instagram', 'twitter', 'facebook']);
  const [postToOptimize, setPostToOptimize] = useState('');
  const [optimizeGoal, setOptimizeGoal] = useState('engagement');
  const [competitorHandle, setCompetitorHandle] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [imageDescription, setImageDescription] = useState('');
  
  // Results states
  const [campaignResult, setCampaignResult] = useState(null);
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [competitorResult, setCompetitorResult] = useState(null);
  const [imagePrompts, setImagePrompts] = useState(null);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get('/marketing/clients?user_id=default_user');
      setClients(res.data.clients || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, []);

  // Fetch subscription status
  const fetchSubscription = useCallback(async () => {
    try {
      const res = await api.get('/marketing/subscription/status?user_id=default_user');
      setSubscription(res.data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchSubscription();
  }, [fetchClients, fetchSubscription]);

  // Create client
  const handleCreateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/marketing/clients?user_id=default_user', newClient);
      if (res.data.success) {
        toast.success('Client created successfully!');
        fetchClients();
        setNewClient({
          name: '', industry: '', description: '', brand_voice: 'professional', target_audience: '',
          social_handles: { instagram: '', twitter: '', facebook: '' }
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create client');
    }
    setLoading(false);
  };

  // Generate Campaign
  const handleGenerateCampaign = async () => {
    if (!selectedClient || !campaignPrompt) {
      toast.error('Select a client and enter campaign details');
      return;
    }
    setLoading(true);
    setCampaignResult(null);
    try {
      const res = await api.post('/marketing/campaign/generate', {
        client_id: selectedClient._id,
        prompt: campaignPrompt,
        duration_days: campaignDays,
        platforms: campaignPlatforms
      });
      setCampaignResult(res.data);
      toast.success('Campaign generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate campaign');
    }
    setLoading(false);
  };

  // Optimize Post
  const handleOptimizePost = async () => {
    if (!selectedClient || !postToOptimize) {
      toast.error('Select a client and enter post content');
      return;
    }
    setLoading(true);
    setOptimizeResult(null);
    try {
      const res = await api.post('/marketing/post/optimize', {
        client_id: selectedClient._id,
        original_post: postToOptimize,
        goal: optimizeGoal
      });
      setOptimizeResult(res.data);
      toast.success('Post optimized!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to optimize post');
    }
    setLoading(false);
  };

  // Competitor Analysis
  const handleAnalyzeCompetitor = async () => {
    if (!selectedClient || !competitorHandle) {
      toast.error('Select a client and enter competitor handle');
      return;
    }
    setLoading(true);
    setCompetitorResult(null);
    try {
      const res = await api.post('/marketing/competitor/analyze', {
        client_id: selectedClient._id,
        competitor_handle: competitorHandle
      });
      setCompetitorResult(res.data);
      toast.success('Analysis complete!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to analyze competitor');
    }
    setLoading(false);
  };

  // Chat with AI
  const handleChat = async () => {
    if (!chatMessage.trim()) return;
    
    const userMsg = { role: 'user', content: chatMessage };
    setChatHistory(prev => [...prev, userMsg]);
    setChatMessage('');
    setLoading(true);
    
    try {
      const res = await api.post('/marketing/chat', {
        client_id: selectedClient?._id || 'general',
        message: chatMessage
      });
      const aiMsg = { role: 'assistant', content: res.data.response };
      setChatHistory(prev => [...prev, aiMsg]);
    } catch (err) {
      toast.error('Chat failed');
    }
    setLoading(false);
  };

  // Generate Image Prompts
  const handleGenerateImagePrompts = async () => {
    if (!selectedClient || !imageDescription) {
      toast.error('Select a client and enter description');
      return;
    }
    setLoading(true);
    setImagePrompts(null);
    try {
      const res = await api.post('/marketing/prompts/image', {
        client_id: selectedClient._id,
        description: imageDescription
      });
      setImagePrompts(res.data);
      toast.success('Prompts generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate prompts');
    }
    setLoading(false);
  };

  // Delete client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await api.delete(`/marketing/clients/${clientId}`);
      toast.success('Client deleted');
      fetchClients();
      if (selectedClient?._id === clientId) setSelectedClient(null);
    } catch (err) {
      toast.error('Failed to delete client');
    }
  };

  const tabs = [
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'campaign', label: '30-Day Campaign', icon: Calendar },
    { id: 'optimizer', label: 'Post Optimizer', icon: Zap },
    { id: 'competitor', label: 'Competitor Spy', icon: Eye },
    { id: 'chat', label: 'AI Chat', icon: MessageSquare },
    { id: 'prompts', label: 'Image Prompts', icon: Image },
    { id: 'scheduler', label: 'Auto Poster', icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-purple-500/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">AstraPulse</h1>
              <p className="text-xs text-purple-300">AI Marketing Command Center</p>
            </div>
          </div>
          
          {/* Subscription Badge */}
          {subscription && (
            <div className="flex items-center gap-4">
              <div className="px-4 py-2 rounded-lg bg-purple-500/20 border border-purple-500/30">
                <span className="text-purple-300 text-sm">Plan: </span>
                <span className="text-white font-semibold capitalize">{subscription.plan}</span>
              </div>
              <div className="text-xs text-gray-400">
                Clients: {subscription.usage?.clients || 0}/{subscription.limits?.clients || 1}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Client Selection */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-black/30 rounded-xl border border-purple-500/20 p-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-400" />
                Select Client
              </h3>
              <div className="space-y-2">
                {clients.map(client => (
                  <button
                    key={client._id}
                    onClick={() => setSelectedClient(client)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                      selectedClient?._id === client._id
                        ? 'bg-purple-500/30 border border-purple-500'
                        : 'bg-white/5 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    <div className="text-white text-sm font-medium truncate">{client.name}</div>
                    <div className="text-gray-400 text-xs truncate">{client.industry}</div>
                  </button>
                ))}
                {clients.length === 0 && (
                  <p className="text-gray-500 text-sm">No clients yet. Create one!</p>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-4 bg-black/30 rounded-xl border border-purple-500/20 p-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Clients Tab */}
            {activeTab === 'clients' && (
              <div className="bg-black/30 rounded-xl border border-purple-500/20 p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-purple-400" />
                  Add New Client
                </h2>
                <form onSubmit={handleCreateClient} className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Brand/Client Name *"
                    value={newClient.name}
                    onChange={e => setNewClient({...newClient, name: e.target.value})}
                    className="col-span-2 bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Industry (e.g., Fitness, Food, Tech)"
                    value={newClient.industry}
                    onChange={e => setNewClient({...newClient, industry: e.target.value})}
                    className="bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none"
                  />
                  <select
                    value={newClient.brand_voice}
                    onChange={e => setNewClient({...newClient, brand_voice: e.target.value})}
                    className="bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual & Friendly</option>
                    <option value="humorous">Humorous</option>
                    <option value="inspirational">Inspirational</option>
                    <option value="luxurious">Luxurious</option>
                  </select>
                  <textarea
                    placeholder="Brand Description"
                    value={newClient.description}
                    onChange={e => setNewClient({...newClient, description: e.target.value})}
                    className="col-span-2 bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none h-20 resize-none"
                  />
                  <input
                    type="text"
                    placeholder="Target Audience"
                    value={newClient.target_audience}
                    onChange={e => setNewClient({...newClient, target_audience: e.target.value})}
                    className="col-span-2 bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Instagram Handle"
                    value={newClient.social_handles.instagram}
                    onChange={e => setNewClient({...newClient, social_handles: {...newClient.social_handles, instagram: e.target.value}})}
                    className="bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Twitter Handle"
                    value={newClient.social_handles.twitter}
                    onChange={e => setNewClient({...newClient, social_handles: {...newClient.social_handles, twitter: e.target.value}})}
                    className="bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="col-span-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Client'}
                  </button>
                </form>

                {/* Client List */}
                <div className="mt-6 pt-6 border-t border-purple-500/20">
                  <h3 className="text-white font-semibold mb-3">Your Clients</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {clients.map(client => (
                      <div key={client._id} className="bg-white/5 rounded-lg p-4 border border-purple-500/20">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-white font-medium">{client.name}</h4>
                            <p className="text-gray-400 text-sm">{client.industry}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteClient(client._id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-gray-500 text-xs mt-2 truncate">{client.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Campaign Generator Tab */}
            {activeTab === 'campaign' && (
              <div className="bg-black/30 rounded-xl border border-purple-500/20 p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-400" />
                  30-Day Campaign Generator
                </h2>
                {!selectedClient ? (
                  <p className="text-yellow-400">Please select a client first</p>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
                      <span className="text-purple-300">Client: </span>
                      <span className="text-white font-semibold">{selectedClient.name}</span>
                    </div>
                    <textarea
                      placeholder="Describe your campaign (e.g., 'Launch new fitness app targeting young professionals with daily workout challenges')"
                      value={campaignPrompt}
                      onChange={e => setCampaignPrompt(e.target.value)}
                      className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none h-32 resize-none"
                    />
                    <div className="flex gap-4">
                      <select
                        value={campaignDays}
                        onChange={e => setCampaignDays(Number(e.target.value))}
                        className="bg-white/5 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:border-purple-500 outline-none"
                      >
                        <option value={7}>7 Days</option>
                        <option value={14}>14 Days</option>
                        <option value={30}>30 Days</option>
                      </select>
                      <div className="flex gap-2">
                        {['instagram', 'twitter', 'facebook', 'linkedin'].map(platform => (
                          <label key={platform} className="flex items-center gap-1 text-gray-300 text-sm">
                            <input
                              type="checkbox"
                              checked={campaignPlatforms.includes(platform)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setCampaignPlatforms([...campaignPlatforms, platform]);
                                } else {
                                  setCampaignPlatforms(campaignPlatforms.filter(p => p !== platform));
                                }
                              }}
                              className="accent-purple-500"
                            />
                            <span className="capitalize">{platform}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={handleGenerateCampaign}
                      disabled={loading || !campaignPrompt}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                      {loading ? 'Generating Campaign...' : 'Generate Campaign'}
                    </button>

                    {campaignResult && (
                      <div className="mt-4 bg-white/5 rounded-lg p-4 border border-green-500/30">
                        <h3 className="text-green-400 font-semibold mb-2">Campaign Generated!</h3>
                        <pre className="text-gray-300 text-sm whitespace-pre-wrap overflow-auto max-h-96">
                          {campaignResult.raw_response}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Post Optimizer Tab */}
            {activeTab === 'optimizer' && (
              <div className="bg-black/30 rounded-xl border border-purple-500/20 p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Post Optimizer - Make It Viral!
                </h2>
                {!selectedClient ? (
                  <p className="text-yellow-400">Please select a client first</p>
                ) : (
                  <div className="space-y-4">
                    <textarea
                      placeholder="Paste your existing post here..."
                      value={postToOptimize}
                      onChange={e => setPostToOptimize(e.target.value)}
                      className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none h-32 resize-none"
                    />
                    <div className="flex gap-4">
                      <select
                        value={optimizeGoal}
                        onChange={e => setOptimizeGoal(e.target.value)}
                        className="bg-white/5 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:border-purple-500 outline-none"
                      >
                        <option value="engagement">Maximize Engagement</option>
                        <option value="clicks">Maximize Clicks</option>
                        <option value="shares">Maximize Shares</option>
                        <option value="saves">Maximize Saves</option>
                      </select>
                    </div>
                    <button
                      onClick={handleOptimizePost}
                      disabled={loading || !postToOptimize}
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
                      {loading ? 'Optimizing...' : 'Make It 10x Viral'}
                    </button>

                    {optimizeResult && (
                      <div className="mt-4 bg-white/5 rounded-lg p-4 border border-yellow-500/30">
                        <h3 className="text-yellow-400 font-semibold mb-2">Optimized Versions!</h3>
                        <pre className="text-gray-300 text-sm whitespace-pre-wrap overflow-auto max-h-96">
                          {optimizeResult.optimized_versions}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Competitor Analysis Tab */}
            {activeTab === 'competitor' && (
              <div className="bg-black/30 rounded-xl border border-purple-500/20 p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-red-400" />
                  Competitor Spy Mode
                </h2>
                {!selectedClient ? (
                  <p className="text-yellow-400">Please select a client first</p>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Enter competitor's social handle (e.g., @nike)"
                      value={competitorHandle}
                      onChange={e => setCompetitorHandle(e.target.value)}
                      className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none"
                    />
                    <button
                      onClick={handleAnalyzeCompetitor}
                      disabled={loading || !competitorHandle}
                      className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Target className="w-5 h-5" />}
                      {loading ? 'Analyzing...' : 'Analyze Competitor'}
                    </button>

                    {competitorResult && (
                      <div className="mt-4 bg-white/5 rounded-lg p-4 border border-red-500/30">
                        <h3 className="text-red-400 font-semibold mb-2">Competitive Analysis</h3>
                        <pre className="text-gray-300 text-sm whitespace-pre-wrap overflow-auto max-h-96">
                          {competitorResult.analysis}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* AI Chat Tab */}
            {activeTab === 'chat' && (
              <div className="bg-black/30 rounded-xl border border-purple-500/20 p-6 h-[600px] flex flex-col">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-green-400" />
                  AI Marketing Assistant
                </h2>
                
                <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                  {chatHistory.length === 0 && (
                    <p className="text-gray-500 text-center py-10">
                      Ask me anything about marketing, content strategy, or social media!
                    </p>
                  )}
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        msg.role === 'user' 
                          ? 'bg-purple-500/30 text-white' 
                          : 'bg-white/10 text-gray-200'
                      }`}>
                        <pre className="whitespace-pre-wrap text-sm font-sans">{msg.content}</pre>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatMessage}
                    onChange={e => setChatMessage(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleChat()}
                    placeholder="Type your message..."
                    className="flex-1 bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none"
                  />
                  <button
                    onClick={handleChat}
                    disabled={loading || !chatMessage.trim()}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 rounded-lg hover:opacity-90 disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Image Prompts Tab */}
            {activeTab === 'prompts' && (
              <div className="bg-black/30 rounded-xl border border-purple-500/20 p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-400" />
                  AI Image/Video Prompt Generator
                </h2>
                {!selectedClient ? (
                  <p className="text-yellow-400">Please select a client first</p>
                ) : (
                  <div className="space-y-4">
                    <textarea
                      placeholder="Describe what you want to create (e.g., 'Product showcase with summer vibes for our new smoothie range')"
                      value={imageDescription}
                      onChange={e => setImageDescription(e.target.value)}
                      className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 outline-none h-24 resize-none"
                    />
                    <button
                      onClick={handleGenerateImagePrompts}
                      disabled={loading || !imageDescription}
                      className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Image className="w-5 h-5" />}
                      {loading ? 'Generating Prompts...' : 'Generate Creative Prompts'}
                    </button>

                    {imagePrompts && (
                      <div className="mt-4 bg-white/5 rounded-lg p-4 border border-blue-500/30">
                        <h3 className="text-blue-400 font-semibold mb-2">Creative Prompts Ready!</h3>
                        <pre className="text-gray-300 text-sm whitespace-pre-wrap overflow-auto max-h-96">
                          {imagePrompts.prompts}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Auto Poster Tab */}
            {activeTab === 'scheduler' && (
              <div className="bg-black/30 rounded-xl border border-purple-500/20 p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-400" />
                  Auto Poster / Scheduler
                </h2>
                <div className="text-center py-10 text-gray-400">
                  <Clock className="w-16 h-16 mx-auto mb-4 text-orange-400/50" />
                  <p>Auto posting feature coming soon!</p>
                  <p className="text-sm mt-2">Schedule posts and let AI post them automatically</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingDashboard;
